import * as React from "react";
import { useEffect, useState } from "react";
import sfetch from "./commons/utils/super-fetch";
import EmailTemplate from "./commons/model/EmailTemplate";
import LoadingSpinner from "./flow/utils/LoadingSpinner";
import ViewAction from "./commons/components/action/ViewAction";
import EditAction from "./commons/components/action/EditAction";
import ToggleButton from "./commons/components/buttons/ToggleButton";
import "./EmailTemplateViewer.scss";
import * as Sentry from "@sentry/browser";
import useFetchService from "./commons/utils/FetchService";
import { sanitize } from "./commons/utils/sanitize";
import RemoveAction from "./commons/components/action/RemoveAction";
import ModalDelete from "./commons/components/modal/ModalDelete";

interface EmailTemplateViewerProps {
  preview: () => void;
  url: string;
  onTemplateDelete: () => void;
}

interface EmailTemplateViewerDisplayProps {
  displayError: boolean;
  displaySuccess: boolean;
  preview: () => void;
  template?: EmailTemplate;
  updateTemplateNotification: (boolean: boolean) => void;
  onTemplateDelete: () => void;
}

export default function EmailTemplateViewer({
  preview,
  url,
  onTemplateDelete,
}: EmailTemplateViewerProps) {
  const [displayError, setDisplayError] = useState(false);
  const [displaySuccess, setDisplaySuccess] = useState(false);
  const [template, setTemplate] = useState<EmailTemplate>();
  const { get } = useFetchService<EmailTemplate>();

  useEffect(() => {
    get(url).then((emailTemplate) => {
      setTemplate(emailTemplate);
    });
    setDisplaySuccess(false);
  }, [url]);

  const updateTemplateNotification = (enabled: boolean) => {
    setDisplayError(false);
    setDisplaySuccess(false);
    sfetch(template.notificationSettingsApiUrl, {
      method: "POST",
      body: JSON.stringify({
        type: template.templateType,
        enabled: enabled,
      }),
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then(() => {
        setTemplate({ ...template, notificationSettingEnabled: enabled });
        setDisplaySuccess(true);
      })
      .catch((error) => {
        Sentry.captureException(error);
        setDisplayError(true);
      });
  };

  return (
    <EmailTemplateViewerDisplay
      displayError={displayError}
      displaySuccess={displaySuccess}
      template={template}
      preview={preview}
      updateTemplateNotification={updateTemplateNotification}
      onTemplateDelete={onTemplateDelete}
    />
  );
}

function EmailTemplateViewerDisplay({
  displayError,
  displaySuccess,
  preview,
  template,
  updateTemplateNotification,
  onTemplateDelete,
}: EmailTemplateViewerDisplayProps) {
  const [displayDeleteDialog, setDisplayDeleteDialog] = useState(false);

  if (template === undefined) {
    return <LoadingSpinner />;
  }

  const deleteTemplate = async () => {
    sfetch(template.deleteUrl, {
      method: "DELETE",
    }).then(() => {
      setDisplayDeleteDialog(false);
      onTemplateDelete();
    });
  };

  let templateClassName = "email-template panel with-shadow";
  if (template.usage !== null) {
    templateClassName = "email-template panel email-template-has-usage-panel with-shadow";
  }

  return (
    <>
      <div className={templateClassName}>
        <div className="react-form">
          <div className="input-group">
            <div className="h-flex space-between">
              <div className="name">{template.name}</div>
              <div className="actions h-flex with-separator">
                {template.isDeletable && template.deleteUrl && (
                  <RemoveAction onClick={() => setDisplayDeleteDialog(true)} />
                )}
                <ViewAction label={i18n["Global.Preview"]} href="#" onClick={preview} />
                {template.apiUrl && (
                  <EditAction href={`${document.location.pathname}/${template.uid}`} />
                )}
              </div>
            </div>
          </div>
          <div className="input-group">
            <label>{i18n["EmailTemplateEditor.Subject.Title"]}</label>
            <div
              dangerouslySetInnerHTML={{
                __html: sanitize(template.subject),
              }}
            />
          </div>
          {displayError && (
            <div className={"notification-save-error-message"}>
              <i className="ic_check_circled" />
              &nbsp;
              {i18n["EmailNotificationSetting.Save.Failed"]}
            </div>
          )}
          {displaySuccess && (
            <div className={"notification-save-success-message"}>
              <i className="ic_check_circled" />
              &nbsp;
              {i18n["EmailNotificationSetting.Save.Success"]}
            </div>
          )}
          <div className="input-group">
            <label>{i18n["EmailTemplateEditor.Content.Title"]}</label>
            <div
              className="rte"
              dangerouslySetInnerHTML={{
                __html: sanitize(template.content),
              }}
            />
          </div>
        </div>
      </div>
      {template.auto && (
        <div className="automatic-email-template-infos">
          <div className="h-flex space-between">
            <div className={"name"}>{i18n["EmailTemplateEditor.Automatic.Title"]}</div>
            <div>
              {template.notificationSettingsApiUrl && (
                <ToggleButton
                  value={
                    template.notificationSettingEnabled === undefined ||
                    template.notificationSettingEnabled
                  }
                  onChange={(newValue) => {
                    updateTemplateNotification(newValue);
                  }}
                  disabled={template.disable}
                />
              )}
            </div>
          </div>
          {template.usage && (
            <div>
              <div className="label">{i18n["Page.Catalog.EmailTemplates.Usage"]}</div>
              <div>{template.usage}</div>
            </div>
          )}
        </div>
      )}
      {displayDeleteDialog && (
        <ModalDelete
          title={i18n["Page.Catalog.EmailTemplates.Delete"]}
          action={deleteTemplate}
          cancel={() => setDisplayDeleteDialog(false)}
        >
          {i18n["Page.Catalog.EmailTemplates.DeleteWarning"]}
        </ModalDelete>
      )}
    </>
  );
}
